import React, { useState } from 'react';
import { useExpenses, useExpenseCategories, useBankAccounts, useInvalidate } from '../hooks/useApi';
import api from '../services/api';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function Expenses() {
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const { data: expensesData, isLoading } = useExpenses({ month });
  const { data: categories } = useExpenseCategories();
  const { data: bankAccounts } = useBankAccounts();
  const { invalidateAll } = useInvalidate();

  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    category_id: '',
    bank_account_id: '',
    expense_date: dayjs().format('YYYY-MM-DD'),
    amount: '',
    description: '',
  });
  const [categoryName, setCategoryName] = useState('');

  const expenses = expensesData?.data || [];
  const totalExpenses = expenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/expenses/${editing.id}`, form);
        toast.success('U përditësua!');
      } else {
        await api.post('/expenses', form);
        toast.success('Shpenzimi u shtua!');
      }
      setShowModal(false);
      setEditing(null);
      setForm({ category_id: '', bank_account_id: '', expense_date: dayjs().format('YYYY-MM-DD'), amount: '', description: '' });
      invalidateAll();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gabim');
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      await api.post('/expense-categories', { name: categoryName });
      toast.success('Kategoria u shtua!');
      setShowCategoryModal(false);
      setCategoryName('');
      invalidateAll();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gabim');
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      category_id: item.category_id,
      bank_account_id: item.bank_account_id || '',
      expense_date: dayjs(item.expense_date).format('YYYY-MM-DD'),
      amount: item.amount,
      description: item.description || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Jeni i sigurt?')) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success('U fshi!');
      invalidateAll();
    } catch (error) {
      toast.error('Gabim');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Shpenzimet</h1>
        <div className="flex gap-2">
          <button onClick={() => setShowCategoryModal(true)} className="btn-secondary">
            + Kategori
          </button>
          <button onClick={() => { setEditing(null); setForm({ category_id: '', bank_account_id: '', expense_date: dayjs().format('YYYY-MM-DD'), amount: '', description: '' }); setShowModal(true); }} className="btn-primary flex items-center gap-2">
            <PlusIcon className="w-5 h-5" /> Shto Shpenzim
          </button>
        </div>
      </div>

      {/* Filter by month */}
      <div className="card">
        <div className="flex items-center gap-4">
          <label className="font-medium">Muaji:</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="input-field w-auto"
          />
          <div className="ml-auto">
            <span className="text-gray-500">Total:</span>
            <span className="ml-2 text-xl font-bold text-red-600">€{totalExpenses.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Expenses table */}
      <div className="card">
        {isLoading ? (
          <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Data</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Kategoria</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Llogaria</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Përshkrim</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Shuma</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Veprime</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense) => (
                  <tr key={expense.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-2">{dayjs(expense.expense_date).format('DD/MM/YYYY')}</td>
                    <td className="py-3 px-2">
                      <span className="px-2 py-1 bg-gray-100 rounded text-sm">{expense.category?.name}</span>
                    </td>
                    <td className="py-3 px-2 text-gray-500">{expense.bank_account?.name || '-'}</td>
                    <td className="py-3 px-2 text-gray-500">{expense.description || '-'}</td>
                    <td className="py-3 px-2 text-right font-medium text-red-600">€{expense.amount}</td>
                    <td className="py-3 px-2 text-right">
                      <button onClick={() => handleEdit(expense)} className="p-1 text-gray-500 hover:text-primary-600"><PencilIcon className="w-5 h-5" /></button>
                      <button onClick={() => handleDelete(expense.id)} className="p-1 text-gray-500 hover:text-red-600 ml-1"><TrashIcon className="w-5 h-5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Expense Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">{editing ? 'Ndrysho' : 'Shto'} Shpenzim</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategoria *</label>
                <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="input-field" required>
                  <option value="">-- Zgjidh --</option>
                  {categories?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Llogaria Bankare</label>
                <select value={form.bank_account_id} onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })} className="input-field">
                  <option value="">-- Zgjidh --</option>
                  {bankAccounts?.map(a => <option key={a.id} value={a.id}>{a.name} (€{a.balance})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                <input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className="input-field" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shuma (€) *</label>
                <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input-field" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Përshkrim</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-field" rows="2" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Anulo</button>
                <button type="submit" className="btn-primary flex-1">Ruaj</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Shto Kategori</h2>
            <form onSubmit={handleAddCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Emri i Kategorisë *</label>
                <input type="text" value={categoryName} onChange={(e) => setCategoryName(e.target.value)} className="input-field" placeholder="p.sh. Qera, Internet, Rroga..." required />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowCategoryModal(false)} className="btn-secondary flex-1">Anulo</button>
                <button type="submit" className="btn-primary flex-1">Shto</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
